/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ActionButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { modalRefVar } from '../../../../../apollo/reactiveVariables/TemplateManagementVariables';
import { TemplatePreviewAdapter } from '../../../../../forms';
import { getAppHost } from '../../../../../utilities/TeamsUtils';
import {
    ErrorEvent,
    FormsRuntimeIframeEventHandlers,
    getFormsTemplatePreviewIframeUrl,
    useFormsRuntimeIframe,
} from '../../../../../utilities/TemplateUtils';
import { isSelfOwnedFormEnabled } from '../../../../../utilities/Utils';
import { ScrollBoundary } from '../../../../common/ScrollBoundary';
import { ErrorPage, ErrorPageProps } from '../../../../containers/ErrorPage';
import { LoadingPage } from '../../../../containers/LoadingPage';
import { TemplateModalHeader, TemplateModalHeaderProps } from '../../../TemplateModalHeader/TemplateModalHeader';
import {
    getBodyStyles,
    getConditionPageWrapperStyles,
    getFooterStyles,
    getHeaderStyles,
    getTemplatePreviewIframeStyles,
    getTemplatePreviewIframeWrapperStyles,
} from './PreviewTemplate.styles';

export interface PreviewTemplateComponentProps {
    onNext?: () => void;
    onBack?: () => void;
    onFormReady?: () => void;
    onError?: (event: ErrorEvent) => void;
    formTemplate: string;
    loading?: boolean;
    error?: ErrorPageProps;
    templateModalHeaderProps: TemplateModalHeaderProps;
    retryCount: number;
}

export const PreviewTemplateComponent: React.FC<PreviewTemplateComponentProps> = (
    props: PreviewTemplateComponentProps
): JSX.Element => {
    const loc = getAppHost().getLocale();

    const { onBack, onNext, onFormReady, onError, formTemplate, templateModalHeaderProps, loading, error, retryCount } =
        props;

    const formsRuntimeIframeUrl = React.useMemo((): string => getFormsTemplatePreviewIframeUrl(), []);
    const formsIFrameEventHandler: FormsRuntimeIframeEventHandlers = {
        getTemplateEventHandler: (): string => formTemplate,
        formReadyEventHandler: (): void => onFormReady && onFormReady(),
        generalErrorHandler: onError,
        serverErrorHandler: onError,
        authErrorHandler: onError,
    };
    const [formsIframeRef, , scrollRef, iframeHeight] = useFormsRuntimeIframe(formsIFrameEventHandler);

    React.useEffect((): void => {
        modalRefVar()?.current?.focus();
    }, []);

    const isErrorOrLoading = !!loading || !!error;

    /* eslint-disable @microsoft/sdl/react-iframe-missing-sandbox */
    return (
        <Stack verticalFill>
            <div role='alert' style={getHeaderStyles()}>
                <TemplateModalHeader {...templateModalHeaderProps} />
            </div>
            <Stack
                verticalFill
                style={{
                    ...getConditionPageWrapperStyles(),
                    visibility: isErrorOrLoading ? 'visible' : 'hidden',
                }}>
                {error ? <ErrorPage {...error} /> : <LoadingPage />}
            </Stack>
            <Stack
                verticalFill
                style={{
                    visibility: isErrorOrLoading ? 'hidden' : 'visible',
                    maxHeight: 'calc(100% - 9.5rem)',
                }}>
                <Stack verticalFill>
                    <div ref={scrollRef} style={getBodyStyles()}>
                        <ScrollBoundary isTopBoundary={true} />
                        {isSelfOwnedFormEnabled() ? (
                            <TemplatePreviewAdapter
                                style={{ padding: '8px 0' }}
                                formTemplate={formTemplate}
                                onFormReady={onFormReady}
                            />
                        ) : (
                            <Stack.Item styles={getTemplatePreviewIframeWrapperStyles(iframeHeight.height)}>
                                <iframe
                                    title={loc.strings.forms_iframe_title}
                                    key={retryCount}
                                    ref={formsIframeRef}
                                    src={formsRuntimeIframeUrl}
                                    style={getTemplatePreviewIframeStyles()}
                                />
                            </Stack.Item>
                        )}
                        <ScrollBoundary isTopBoundary={false} />
                    </div>
                </Stack>
            </Stack>
            <Stack.Item styles={getFooterStyles()}>
                <Stack horizontal verticalAlign='center' horizontalAlign='space-between'>
                    <ActionButton
                        iconProps={{ iconName: 'ChevronLeft' }}
                        text={loc.strings.carousel_back}
                        ariaLabel={loc.strings.carousel_back_aria_label}
                        onClick={onBack}
                    />
                    <PrimaryButton
                        ariaLabel={loc.strings.choose_template_aria_label}
                        text={loc.strings.step_next}
                        onClick={onNext}
                    />
                </Stack>
            </Stack.Item>
        </Stack>
    );
    /* eslint-enable @microsoft/sdl/react-iframe-missing-sandbox */
};
